/**
 * Folder Page with Hierarchical Breadcrumb Navigation
 *
 * This component provides folder navigation with a dynamic breadcrumb system that:
 *
 * 1. Parses folder paths like "/moamenfnnana1@gmail.com/home/aaa/folder 2"
 * 2. Generates breadcrumbs: Home (with home icon) > aaa > folder 2
 * 3. Handles navigation for root and current folders
 * 4. Provides fallback navigation for intermediate folders
 *
 * NAVIGATION FLOW:
 *
 * When clicking on breadcrumb items:
 *
 * 1. HOME: Direct navigation to /dashboard
 * 2. CURRENT FOLDER: Direct navigation using folder ID
 * 3. INTERMEDIATE FOLDERS (e.g., "aaa" when in "folder 2"):
 *    - Step 1: Call useGetFile({ path: "/moamenfnnana1@gmail.com/home/aaa" })
 *    - Step 2: Get folder ID from response (e.g., "123")
 *    - Step 3: Navigate to /folders/123
 *    - Step 4: useSearchFilesInfinite({ parentId: "123" }) fetches files
 *
 * BREADCRUMB FEATURES:
 * - Automatic path parsing and breadcrumb generation
 * - Click navigation to any breadcrumb level
 * - Drag and drop support for moving files to breadcrumb folders
 * - Visual feedback with hover effects and current folder highlighting
 * - Responsive design with truncation for long folder names
 *
 * USAGE:
 * - Click "Home" to navigate to dashboard
 * - Click any folder name to navigate to that folder (if available)
 * - Drag files onto breadcrumb items to move them to that folder
 *
 * TODO:
 * - Add caching for folder IDs to improve performance
 * - Add toast notifications for navigation feedback
 */

import { getFile, MediaType, queryKeys, useGetFile } from '@qse/api'
import { useUIStore } from '@qse/stores'
import { Breadcrumb, toast, useGlobalFileUpload } from '@qse/ui'
import type { FileItem } from '@qse/utils'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { FileExplorer } from '@/components'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface BreadcrumbItem {
  id: string
  name: string
  path?: string // backend path for API
  routePath?: string // frontend route for navigation
  rootPath?: string // root path for Home breadcrumb
}

interface SearchValues {
  search: string
  fileType?: string
  createdFrom?: string
  createdTo?: string
}

const parseFolderPath = (path: string): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  if (!path) return items

  // Split the path by '/' and filter out empty segments
  const pathSegments = path.split('/').filter(segment => segment.trim() !== '')

  // Build the breadcrumb path progressively
  // We need to construct the full path including email and home for API calls
  let currentPath = pathSegments.length >= 2 ? `/${pathSegments[0]}/home` : '/home'

  // Skip the email part (first segment) and 'home' (second segment)
  // Start from index 2 to skip email and 'home'
  const folderSegments = pathSegments.slice(2)

  folderSegments.forEach((segment, index) => {
    // Create a unique ID for each breadcrumb item
    const itemId = `folder-${index}-${segment}`

    // Build the path for this level (include full path for API calls)
    currentPath += `/${segment}`

    // Store the full path for API navigation
    items.push({
      id: itemId,
      name: segment,
      path: currentPath,
    })
  })

  return items
}

const Folder = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { id } = useParams<{ id: string }>()
  const query = useQuery()
  const searchQuery = query.get('query') || ''
  const fileType = query.get('fileType') || ''
  const isDir = query.get('isDir') || ''
  const createdFrom = query.get('createdFrom') || ''
  const createdTo = query.get('createdTo') || ''

  const queryClient = useQueryClient()
  const { setDisableNewButton, setIsSystemFolder } = useUIStore()

  // Get folder data from location state if navigating from search/list
  const folderDataFromState = (location.state as any)?.folderDetails
  const hasFolderDataFromState = !!folderDataFromState

  // Fetch folder details using the ID from route
  // Only fetch if we don't have data from location state (i.e., direct URL or refresh)
  const {
    data: fetchedFolderDetails,
    isLoading: folderLoading,
    error: folderError,
  } = useGetFile({
    id,
    enabled: !hasFolderDataFromState, // Only fetch if we don't have data from state
  })

  // If we have data from state, we don't need to show loading state
  const actualFolderLoading = hasFolderDataFromState ? false : folderLoading

  // Use folder data from state if available, otherwise use fetched data
  const folderDetails = folderDataFromState || fetchedFolderDetails

  const { addFiles } = useGlobalFileUpload()

  // Disable New button when in system folders or when loading folder data
  useEffect(() => {
    const isSystem = !!folderDetails?.systemFolderType
    const isLoading = actualFolderLoading

    setIsSystemFolder(isSystem)

    // Disable button for system folders OR when loading folder data
    if (isSystem || isLoading) {
      setDisableNewButton(true)
    } else {
      setDisableNewButton(false)
    }

    // Cleanup on unmount
    return () => {
      setDisableNewButton(false)
      setIsSystemFolder(false)
    }
  }, [folderDetails?.systemFolderType, actualFolderLoading, setDisableNewButton, setIsSystemFolder])

  // Handle folder not found error - redirect to dashboard
  useEffect(() => {
    if (folderError && !actualFolderLoading) {
      // Check if it's a 404 error (folder not found or deleted)
      const errorStatus = (folderError as any)?.status || (folderError as any)?.response?.status

      if (errorStatus === 404) {
        // Show a specific message for folder not found
        toast.error('Folder not found', {
          description: 'This folder may have been deleted or moved. Redirecting to My Drive...',
        })

        // Redirect to dashboard after a short delay
        const timer = setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 2000)

        return () => clearTimeout(timer)
      }
    }
  }, [folderError, actualFolderLoading, navigate])

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    // If there's an error, return empty breadcrumbs (page will redirect anyway)
    if (folderError) {
      return []
    }

    if (folderDetails?.path) {
      // Use the path to generate proper hierarchical breadcrumbs
      const items = parseFolderPath(folderDetails.path)

      // Get the root path for the Home breadcrumb
      const pathSegments = folderDetails.path
        .split('/')
        .filter((segment: string) => segment.trim() !== '')
      const rootPath = pathSegments.length >= 2 ? `/${pathSegments[0]}/home` : '/home'

      return items.map(item => ({
        ...item,
        routePath: `/folders/${item.id}`, // Optionally keep for navigation, not used in API
        rootPath, // Add root path for Home breadcrumb
      }))
    } else if (folderDetails) {
      // Fallback: create breadcrumb with just the current folder
      const pathSegments =
        folderDetails.path?.split('/').filter((segment: string) => segment.trim() !== '') || []
      const rootPath = pathSegments.length >= 2 ? `/${pathSegments[0]}/home` : '/home'

      return [
        {
          id: folderDetails.id,
          name: folderDetails.name || folderDetails.path?.split('/').pop() || 'Folder',
          path: folderDetails.path, // backend path
          routePath: `/folders/${folderDetails.id}`,
          rootPath, // Add root path for Home breadcrumb
        },
      ]
    }

    // If no folder details and no error, return empty while loading
    return []
  }, [folderDetails, id, folderError])

  const handleBreadcrumbClick = async (item: BreadcrumbItem) => {
    if (item.id === 'root') {
      navigate('/dashboard')

      return
    }
    if (item.path) {
      // item.path is now always the backend path
      try {
        const folder = await getFile({ path: item.path })

        if (folder?.id) {
          navigate(`/folders/${folder.id}`, { state: { folderDetails: folder } })
        } else {
          toast.error('Folder not found')
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to navigate to folder')
      }
    }
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    if (files.length > 0) {
      addFiles(files, id, [queryKeys.files.list({ parentId: id })], queryClient)
    }
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const searchValues = useMemo<SearchValues>(
    () => ({
      search: searchQuery,
      fileType: fileType || undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    }),
    [searchQuery, fileType, createdFrom, createdTo],
  )

  // Search parameters for FileExplorer
  const searchParams = useMemo(
    () => ({
      limit: 50,
      parentId: id,
      search: searchValues.search || undefined,
      mediaType: (searchValues.fileType as MediaType) || undefined,
      isDir: isDir === 'true' ? true : undefined,
      createdFrom: searchValues.createdFrom || undefined,
      createdTo: searchValues.createdTo || undefined,
    }),
    [searchValues, id, isDir],
  )

  const emptyStateConfig = {
    icon: (
      <svg
        className="w-12 h-12 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    title: 'This folder is empty',
    description: 'Upload files or create subfolders to get started.',
  }

  return (
    <div className="flex flex-col gap-2 text-gray-900 dark:text-white">
      {/* Breadcrumb Navigation */}
      <div className="px-4 md:px-[30px] sticky top-[138px] z-20">
        <Breadcrumb
          items={breadcrumbItems}
          loading={actualFolderLoading}
          onItemClick={handleBreadcrumbClick}
        />
      </div>

      {/* Hidden file input for upload */}
      <input
        ref={fileInputRef}
        multiple
        className="hidden"
        type="file"
        onChange={handleFileInputChange}
      />

      <div className="flex-1">
        <FileExplorer
          breadcrumbItems={breadcrumbItems}
          emptyStateConfig={emptyStateConfig}
          error={folderError}
          isLoading={actualFolderLoading}
          parentId={id}
          parentName={folderDetails?.name}
          parentType={folderDetails?.systemFolderType}
          searchParams={searchParams}
          title=""
          uploadEnabled={true}
          onFileDoubleClick={(file: FileItem) => {
            if (file && (file.isDir || file.type === 'folder')) {
              navigate(`/folders/${file.id}`, { state: { folderDetails: file } })
            }
          }}
          onRetry={() => {
            // Refetch folder details
            window.location.reload()
          }}
        />
      </div>
    </div>
  )
}

export default Folder
